import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  DialogActions,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import { styled } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import CompanyStage from "components/CompanyStage";
import { ROUTES } from "helpers/routes";
import storageUtils from "helpers/storageUtils";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCompany } from "services/hooks/useCompany";
import theme from "theme";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return (
    <Slide direction="up" ref={ref} {...props}>
      {props.children as React.ReactElement}
    </Slide>
  );
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  paddingBlock: theme.spacing(2),
  paddingInline: theme.spacing(6),
  position: "relative",
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  right: 0,
  position: "absolute",
  backgroundColor: theme.palette.common.white,
  top: theme.spacing(1),
  transform: "translate(150%, -50%)",
  "&:hover": {
    backgroundColor: theme.palette.common.white,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(6),
}));

const CompanyDetails = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  const [currentVisitor, setCurrentVisitor] = useState<any>();
  const { data: company } = useCompany(slug??'');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const visitorName = `${currentVisitor?.firstName} ${currentVisitor?.lastName}`;
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      if (window.history?.length && window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(ROUTES.HOME, { replace: true });
      }
    }, 100);
  };

  const handleOnContactClick = () => {
    if (!currentVisitor) {
      navigate(ROUTES.CHECK_IN);
    } else {
      setShowConfirmDialog(true);
    }
  };

  useEffect(() => {
    if (company !== undefined) {
      setTimeout(() => setOpen(true), 100);
    }
  }, [company]);

  useEffect(() => {
    const visitor = storageUtils.getVisitorFromLocalStorage();
    if (visitor) {
      setCurrentVisitor(visitor);
    }
  }, []);
  console.log("currentVisitor: ", currentVisitor);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      PaperProps={{
        style: {
          borderRadius: 30,
          width: "80%",
          overflow: "visible",
        },
      }}
      onClose={handleClose}
      TransitionComponent={Transition}
      aria-labelledby="customized-dialog-title"
    >
      <StyledDialogTitle id="customized-dialog-title">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack spacing={2} direction="row">
            <img
              src={`${process.env.REACT_APP_IMAGE_URL}${company?.logo?.url}`}
              alt={company?.name}
              style={{
                height: 100,
                padding: theme.spacing(2),
                borderRadius: theme.spacing(2),
                border: `1px solid ${theme.palette.divider}`,
                objectFit: "contain",
              }}
            />
            <Stack justifyContent="space-between">
              <Typography variant="h4">{company?.name}</Typography>
              <Stack spacing={2} direction="row">
                {company?.industries?.map((industry, index) => (
                  <Typography variant="subtitle1" color="textSecondary">
                    {industry.name}
                  </Typography>
                ))}
              </Stack>
              {company?.stage && <CompanyStage stage={company?.stage} />}
            </Stack>
          </Stack>
          <Button
            variant="contained"
            style={{ paddingInline: theme.spacing(4) }}
            onClick={handleOnContactClick}
          >
            Contact
          </Button>
        </Stack>
        <StyledIconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </StyledIconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={8} direction={{ xs: "column", lg: "row" }}>
          <Stack spacing={2} style={{ maxWidth: 500 }}>
            <Stack>
              <Typography gutterBottom variant="h5" color="textSecondary">
                Description
              </Typography>
              <Typography gutterBottom>{company?.description}</Typography>
            </Stack>
            <Box>
              <Typography gutterBottom variant="h5" color="textSecondary">
                Market:
              </Typography>
              <Typography component="span" variant="body1">
                {company?.market}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1} flexGrow={1}>
            <Stack>
              <Typography gutterBottom variant="h5" color="textSecondary">
                {company?.founder?.title}
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 700 }}>
                {company?.founder?.name}
              </Typography>
            </Stack>
            <Stack
              spacing={2.5}
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <Box style={{ aspectRatio: 300 / 242 }}>
                <img
                  width={300}
                  height="auto"
                  src={`${process.env.REACT_APP_IMAGE_URL}${company?.founder?.photo?.url}`}
                  alt={company?.name}
                />
              </Box>
              <Stack>
                <Stack
                  component="ul"
                  spacing={1}
                  style={{ fontSize: theme.typography.fontSize }}
                >
                  {company?.founder?.past?.map((p, index) => (
                    <Box
                      component="li"
                      key={index}
                      dangerouslySetInnerHTML={{ __html: p.title }}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  {company?.founder?.past?.map((p, _idx) => {
                    return p.logo?.map((logo, index) => (
                      <img
                        style={{ maxWidth: 100 }}
                        key={`${_idx}_${index}`}
                        src={`${process.env.REACT_APP_IMAGE_URL}${logo?.url}`}
                        alt={p.title}
                      />
                    ));
                  })}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </StyledDialogContent>
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="responsive-dialog-title"
      >
        {/* <DialogTitle id="responsive-dialog-title">
          {"Use Google's location service?"}
        </DialogTitle> */}
        <DialogContent>
          <DialogContentText>
            Are you <b>{visitorName}</b>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => navigate(ROUTES.CHECK_IN)}>
            No
          </Button>
          <Button autoFocus onClick={() => navigate(ROUTES.SELECT_TOPIC)}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CompanyDetails;
